import React from 'react';
import { ScrollView, View } from 'react-native';
import styled from 'styled-components/native';
import { useRoute, RouteProp } from '@react-navigation/native';
import { Text, Checkbox, Card } from '@ui';
import { useChecklistStore } from '@stores/checklistStore';
import { JourneyTopTabParamList } from '@navigation/types';
import { format } from 'date-fns';

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const ChecklistCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const DueDateBadge = styled.View<{ overdue: boolean }>`
  background-color: ${({ theme, overdue }) =>
    overdue ? theme.colors.errorLight : theme.colors.infoLight};
  padding: ${({ theme }) => theme.spacing.xs}px ${({ theme }) => theme.spacing.sm}px;
  border-radius: ${({ theme }) => theme.radius.xs}px;
  margin-top: ${({ theme }) => theme.spacing.xs}px;
  align-self: flex-start;
`;

const CategoryBadge = styled.View`
  background-color: ${({ theme }) => theme.colors.neutral100};
  padding: ${({ theme }) => theme.spacing.xs}px ${({ theme }) => theme.spacing.sm}px;
  border-radius: ${({ theme }) => theme.radius.full}px;
  align-self: flex-start;
`;

type ChecklistRouteProp = RouteProp<JourneyTopTabParamList, 'Checklists'>;

const JourneyChecklistsTab: React.FC = () => {
  const route = useRoute<ChecklistRouteProp>();
  const { journeyId } = route.params;
  const { getItemsByJourney, toggleItem } = useChecklistStore();

  const items = getItemsByJourney(journeyId);
  const completedCount = items.filter(item => item.completed).length;

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <Container>
      <Content showsVerticalScrollIndicator={false}>
        {/* Summary */}
        <Card padding="md" style={{ marginBottom: 16 }}>
          <Text variant="titleMedium" color="textPrimary">
            Progress: {completedCount} of {items.length}
          </Text>
          <Text variant="bodyMedium" color="textSecondary" style={{ marginTop: 4 }}>
            Keep going! You're doing great.
          </Text>
        </Card>

        {/* Checklist Items */}
        {items.map(item => (
          <ChecklistCard key={item.id} padding="none">
            <View style={{ padding: 16 }}>
              <Checkbox
                checked={item.completed}
                onChange={() => toggleItem(journeyId, item.id)}
                label={item.title}
                description={item.description}
              />
              <View style={{ marginTop: 8, flexDirection: 'row', gap: 8 }}>
                <CategoryBadge>
                  <Text variant="caption" color="textSecondary">
                    {item.category}
                  </Text>
                </CategoryBadge>
                {item.dueDate && (
                  <DueDateBadge overdue={isOverdue(item.dueDate)}>
                    <Text variant="caption" color={isOverdue(item.dueDate) ? 'error' : 'info'}>
                      Due: {format(new Date(item.dueDate), 'MMM dd, yyyy')}
                    </Text>
                  </DueDateBadge>
                )}
              </View>
            </View>
          </ChecklistCard>
        ))}

        {items.length === 0 && (
          <Card padding="lg">
            <Text variant="body" color="textSecondary" align="center">
              No checklist items yet. Check back soon!
            </Text>
          </Card>
        )}

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default JourneyChecklistsTab;
